import BlockMedia from 'components/BlockMedia';
import { ScButton, type ValidSitecoreLink } from 'components/SitecoreLink';
import type { GTMItemListId, GTMItemListName } from 'hooks/gtm';
import type { DigizuiteAsset } from 'models/asset';
import type { ProductCard } from 'models/productCard';
import type { ColorString } from 'types';
import { cn } from 'utils/classNames';
import { is } from 'utils/helpers';

interface Props {
	/** Background color for any base price text */
	basePriceTextBackground?: ColorString;

	campaignBubbleLabel?: string;
	campaignBubbleSale?: boolean;
	campaignBubbleText?: string;

	/** GTM list ID for the products. */
	gtmItemListId: GTMItemListId;

	/** GTM list name for the products. */
	gtmItemListName: GTMItemListName;

	/** Alt for large image */
	largeImageAlt: string | undefined;

	/** Image src for large devices */
	largeImageSrc: string;

	/** Links to render as chips */
	links?: ValidSitecoreLink[];

	/** Don't autoplay video and/or product slideshow */
	preventAutoplay?: boolean;

	primaryLinkHref?: string;

	/** Products to display in an animated slideshow */
	products?: ProductCard[];

	/** Alt for small image */
	smallImageAlt: string | undefined;

	/** Image src for small devices */
	smallImageSrc: string;

	/** Video, takes precedence over image */
	video: DigizuiteAsset | undefined;
}

function Links({
	className,
	links,
}: {
	className?: string;
	links: ValidSitecoreLink[] | undefined;
}) {
	if (!is.arrayWithLength(links)) {
		return null;
	}
	return (
		<div className={cn('flex flex-wrap gap-4 px-4 pt-4', className)}>
			{links.map((link) => (
				<ScButton
					key={link.href}
					variant="secondary"
					link={link}
					className="focus-visible:focus-outline-inverted"
				>
					{link.text}
				</ScButton>
			))}
		</div>
	);
}
Links.displayName = 'PromoHero_Links';

export default function PromoHero({
	basePriceTextBackground,
	campaignBubbleLabel,
	campaignBubbleSale,
	campaignBubbleText,
	gtmItemListId,
	gtmItemListName,
	largeImageAlt,
	largeImageSrc,
	links,
	preventAutoplay,
	primaryLinkHref,
	products,
	smallImageAlt,
	smallImageSrc,
	video,
}: Props) {
	return (
		<div className="relative overflow-hidden">
			<BlockMedia
				image={[
					{
						src: smallImageSrc,
						alt: smallImageAlt,
						variant: 'landscape4x3Small',
						className: 'md:hidden',
					},
					{
						src: largeImageSrc,
						alt: largeImageAlt,
						variant: 'landscape4x1',
						className: 'max-md:hidden',
					},
				]}
				video={video}
				campaignBubbleLargeSize="large"
				campaignBubbleLabel={campaignBubbleLabel}
				campaignBubbleSale={campaignBubbleSale}
				campaignBubbleText={campaignBubbleText}
				products={products}
				productsDisplayType="campaignSpinner"
				preventAutoplay={preventAutoplay}
				href={primaryLinkHref}
				gtmItemListId={gtmItemListId}
				gtmItemListName={gtmItemListName}
				basePriceTextBackground={basePriceTextBackground}
				productAnimationPlacementClassName={cn(
					'flex justify-center',
					'max-md:inset-x-24 max-md:top-24 max-md:bottom-8',
					'md:top-12 md:right-0 md:bottom-8 md:w-2/4',
				)}
				beforeButtonsContent={
					<Links
						links={links}
						className="absolute inset-x-0 bottom-4 z-3 justify-center max-md:hidden"
					/>
				}
			/>
			<Links links={links} className="flex-col items-start md:hidden" />
		</div>
	);
}
PromoHero.displayName = 'PromoHero';
